import * as CSS from "csstype";
import { Button, Label, TextField } from "@metricell/smarttoolscore";
import {
    ImageCropper,
    INotificationProviderRef,
    ISubWindowContent,
    Loading,
    NotificationProvider,
    SubWindow,
} from "./Components";
import React, { useEffect, useRef, useState } from "react";
import { StringUtil, Utils } from "../Utilities";
import { IReturnModel } from ".";
import { IUserProfileDetails } from "./SideMenu";
import "@metricell/smarttoolscore/dist/module/Styles/metricell.css";

interface ISideNavOptions {
    id: number;
    imgURL: string;
    label: string;
}

interface IEditUserProfile extends IUserProfileDetails {
    createdDate: string;
    lastLockoutDate: string;
    lastLoginDate: string;
    lastPasswordChangedDate: string;
    passwordExpiryDate: string;
}

interface IChangeLog {
    byuser: string;
    affecteduser: string;
    change: string;
    description: string;
    section: number;
}

const defaultUserProfileSection: IEditUserProfile = {
    createdDate: ``,
    department: ``,
    email: ``,
    firstName: ``,
    icon: `/Images/OperatorIcon`, // Default to the OperatorIcon just in case the user does not have one
    isPermissionProfileInfoEditEnabled: false,
    isPermissionProfilePictureEditEnabled: false,
    lastLockoutDate: ``,
    lastLoginDate: ``,
    lastName: ``,
    lastPasswordChangedDate: ``,
    passwordExpiryDate: ``,
    phoneNumber: ``,
    userTitle: ``,
    username: ``,
};

export const EditProfileRoot: React.FunctionComponent =
    (): React.ReactElement => {
        const notificationProviderRef = useRef<INotificationProviderRef>(),
            [loading, setLoading] = useState<boolean>(false),
            [isTenancy, setIsTenancy] = useState<boolean>(false),
            [cropperOpen, setCropperOpen] = useState<boolean>(false),
            [selectedSection, setSelectedSection] = useState<number>(0),
            [userProfile, setUserProfile] = useState<IEditUserProfile>(
                defaultUserProfileSection,
            ),
            [updatedUserProfile, setUpdatedUserProfile] =
                useState<IEditUserProfile>(defaultUserProfileSection),
            [imageReader, setImageReader] = useState<FileReader>(
                new FileReader(),
            ),
            [subWindowProps, setSubWindowProps] =
                useState<ISubWindowContent>(null),
            [prefixChange, setPrefixChange] = useState<boolean>(false),
            [numberChange, setNumberChange] = useState<boolean>(false),
            returnURL =
                window.location.origin +
                new URLSearchParams(window.location.search)
                    .get(`ReturnURL`)
                    .replace(`%2f`, `/`),
            sideNavOptions: ISideNavOptions[] = [
                {
                    id: 0,
                    imgURL: `/Images/Icon?icon=230100&folder=Module`,
                    label: `My Profile`,
                },
                {
                    id: 1,
                    imgURL: `/Images/LogoutSectionIcon.svg`,
                    label: `Logout`,
                },
            ];

        const countrycodeRegex = new RegExp(
                /^\+?(([0-9][-~][0-9]{0,3})|([0-9]{1,3}))\*?$/,
            ),
            emailRegex = new RegExp(
                /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/,
            ),
            mobileRegex = new RegExp(/^[0-9]{7,12}$/),
            plainTextOrEmptyRegex = new RegExp(/^[a-z A-Z]*$/),
            plainTextRegex = new RegExp(/^[a-z A-Z]+$/);

        let imageReaderTemp: FileReader,
            imageTemp: HTMLImageElement,
            inputRef: HTMLInputElement;

        useEffect(() => {
            void getMyProfile();
        }, []);

        const getMyProfile = async (): Promise<void> => {
            setLoading(true);
            const tenancy: string = await $.ajax({
                method: `GET`,
                url: `/Profile/TenancyCheck`,
            });

            setIsTenancy(tenancy === `True`); // Because ajax stringifies things

            const stringResult: string = await $.ajax({
                method: `GET`,
                url: `/Profile/EditMyProfile`,
            });

            const result: IReturnModel<IEditUserProfile> =
                JSON.parse(stringResult);

            if (result.isSuccess) {
                setUserProfile((oldUserProfile) => ({
                    ...oldUserProfile,
                    ...result.value,
                }));

                setUpdatedUserProfile((oldUserProfile) => ({
                    ...oldUserProfile,
                    ...result.value,
                }));
            }
            setLoading(false);
        };

        const selectNewIcon = (): void => {
            document.getElementById(`UserProfileImageDialog`).click();
        };

        const readNewIcon = (
            input: React.ChangeEvent<HTMLInputElement>,
        ): void => {
            const inputImage: File = input.target.files[0];

            imageReaderTemp = new FileReader();
            imageReaderTemp.readAsDataURL(inputImage);
            imageReaderTemp.onload = loadNewIcon;
            setImageReader(imageReaderTemp);
            inputRef.value = null;
        };

        const loadNewIcon = (input: ProgressEvent): void => {
            if (input.returnValue) {
                imageTemp = document.createElement(`img`);
                imageTemp.src = imageReaderTemp.result.toString();
                imageTemp.onload = openCropper;
            }
        };

        const openCropper = (): void => {
            setSubWindowProps({
                body: (
                    <ImageCropper
                        imageAsDataUri={imageReaderTemp.result.toString()}
                        imageResolution={{
                            x: imageTemp.width,
                            y: imageTemp.height,
                        }}
                        updateImage={updateImage}
                    />
                ),
                isDominant: true,
                moduleID: -10001,
                subWindowKey: `profileCropperWindow`,
                title: `Crop Image`,
            });
            setCropperOpen(true);
        };

        const updateImage = (imageUri: string): void => {
            setUpdatedUserProfile({
                ...updatedUserProfile,
                icon: imageUri,
            });
            closeTowerSubWindow();
        };

        const closeTowerSubWindow = (): void => {
            setCropperOpen(false);
        };

        const sideMenuClickEvent = (key: number): void => {
            if (key === 1) {
                location.href = `/Account/Logout`;
            } else {
                setSelectedSection(key);
            }
        };

        const saveChanges = async (): Promise<void> => {
            await $.ajax({
                data: {
                    userProfile: updatedUserProfile,
                },
                error: (error) => {
                    if (error.status === 409) {
                        notificationProviderRef.current.addNotificationToQueue({
                            message: `Failed to update, the email address ${updatedUserProfile.email} is already in use.`,
                            severity: `error`,
                        });

                        return;
                    }

                    notificationProviderRef.current.addNotificationToQueue({
                        message: `An error has occurred.`,
                        severity: `error`,
                    });
                },
                method: `POST`,
                success: () => {
                    setPrefixChange(false);
                    setNumberChange(false);
                    void saveChangeLogEditedProfile(
                        userProfile,
                        updatedUserProfile,
                    );
                    setUserProfile(updatedUserProfile);
                    notificationProviderRef.current.addNotificationToQueue({
                        message: `Changes have been saved.`,
                        severity: `info`,
                    });
                },
                url: `/Profile/UpdateProfileDetail`,
            });
        };

        const isUserValid = (user: IEditUserProfile): boolean =>
            isFirstNameValid(user.firstName) &&
            isLastNameValid(user.lastName) &&
            isJobTitleValid(user.userTitle) &&
            isDepartmentValid(user.department) &&
            isCountryCodeValid(getCountryCode(user.phoneNumber)) &&
            isMobileValid(getMobileNumber(user.phoneNumber)) &&
            isEmailValid(user.email);

        const isFirstNameValid = (firstName: string): boolean =>
            isValidString(firstName);

        const isLastNameValid = (lastName: string): boolean =>
            isValidString(lastName);

        const isJobTitleValid = (jobTitle: string): boolean =>
            isValidString(jobTitle);

        const isDepartmentValid = (department: string): boolean =>
            isValidString(department);

        const isCountryCodeValid = (countryCode: string | null): boolean =>
            !StringUtil.isNullOrEmpty(countryCode) &&
            countrycodeRegex.test(countryCode);

        const isMobileValid = (mobile: string): boolean =>
            !StringUtil.isNullOrEmpty(mobile) &&
            mobileRegex.test(mobile.replace(/\s*/g, ``));

        const isEmailValid = (email: string): boolean =>
            !StringUtil.isNullOrEmpty(email) && emailRegex.test(email);

        const isValidString = (text: string): boolean =>
            !StringUtil.isNullOrEmpty(text) && plainTextRegex.test(text);

        const getCountryCode = (phoneNumber: string): string =>
            phoneNumber?.includes(` `) ? phoneNumber?.split(` `)[0] : ``;

        const getMobileNumber = (phoneNumber: string): string =>
            !StringUtil.isNullOrEmpty(phoneNumber)
                ? phoneNumber.substr(phoneNumber.indexOf(` `) + 1)
                : ``;

        const resetPassword = (): void => {
            void $.ajax({
                data: {
                    // eslint-disable-next-line @typescript-eslint/naming-convention, quote-props
                    __RequestVerificationToken: $(
                        `input[name=__RequestVerificationToken]`,
                    ).val(),
                    userName: userProfile.username,
                },
                error: () => {
                    notificationProviderRef.current.addNotificationToQueue({
                        message: `An error has occurred.`,
                        severity: `error`,
                    });
                },
                success: () => {
                    notificationProviderRef.current.addNotificationToQueue({
                        message: `Instructions to reset your password have been sent to the linked email address.`,
                        severity: `info`,
                    });
                },
                type: `POST`,
                url: `Account/ForgotPasswordLogic`,
            });
        };

        const updateCountryCode = (countryCode: string): void => {
            setPrefixChange(true);
            setUpdatedUserProfile((oldState) => {
                const newPhoneNumber = oldState.phoneNumber?.includes(` `)
                    ? `${countryCode} ${oldState.phoneNumber.substr(
                          oldState.phoneNumber.indexOf(` `) + 1,
                      )}`
                    : `${countryCode} ${
                          oldState.phoneNumber === null
                              ? ``
                              : oldState.phoneNumber
                      }`;

                return {
                    ...oldState,
                    phoneNumber: newPhoneNumber,
                };
            });
        };

        const updateMobileNumber = (mobileNumber: string): void => {
            setNumberChange(true);
            setUpdatedUserProfile((oldState) => {
                const newPhoneNumber = oldState.phoneNumber?.includes(` `)
                    ? `${oldState.phoneNumber.split(` `)[0]} ${mobileNumber}`
                    : `${mobileNumber}`;

                return {
                    ...oldState,
                    phoneNumber: newPhoneNumber,
                };
            });
        };

        const getUserProfileChanges = (
            oldProfile: IEditUserProfile,
            updatedProfile: IEditUserProfile,
        ): string => {
            let changes = ``;

            if (oldProfile.firstName !== updatedProfile.firstName) {
                changes += `First Name changed from ${oldProfile.firstName} to ${updatedProfile.firstName}. `;
            }

            if (oldProfile.lastName !== updatedProfile.lastName) {
                changes += `Last Name changed from ${oldProfile.lastName} to ${updatedProfile.lastName}. `;
            }

            if (
                oldProfile.userTitle === undefined ||
                oldProfile.userTitle === `` ||
                oldProfile.userTitle === null
            ) {
                changes += `Job Title changed from '' to ${updatedProfile.userTitle}. `;
            } else if (oldProfile.userTitle !== updatedProfile.userTitle) {
                changes += `Job Title changed from ${oldProfile.userTitle} to ${updatedProfile.userTitle}. `;
            }

            if (
                oldProfile.department === undefined ||
                oldProfile.department === `` ||
                oldProfile.department === null
            ) {
                changes += `Job department changed from '' to ${updatedProfile.department}. `;
            } else if (oldProfile.department !== updatedProfile.department) {
                changes += `Job department changed from ${oldProfile.department} to ${updatedProfile.department}. `;
            }

            if (
                oldProfile.email === undefined ||
                oldProfile.email === `` ||
                oldProfile.email === null
            ) {
                changes += `Email address changed from '' to ${updatedProfile.email}. `;
            } else if (oldProfile.email !== updatedProfile.email) {
                changes += `Email address changed from ${oldProfile.email} to ${updatedProfile.email}. `;
            }

            if (
                oldProfile.phoneNumber === undefined ||
                oldProfile.phoneNumber === `` ||
                oldProfile.phoneNumber === null
            ) {
                changes += `Phone number changed from '' to ${updatedProfile.phoneNumber}. `;
            } else if (oldProfile.phoneNumber !== updatedProfile.phoneNumber) {
                changes += `Phone number changed from ${oldProfile.phoneNumber} to ${updatedProfile.phoneNumber}. `;
            }

            if (oldProfile.icon !== updatedProfile.icon) {
                changes += `Picture changed. `;
            }

            return changes;
        };

        const saveChangeLogEditedProfile = async (
            oldProfile: IEditUserProfile,
            updatedProfile: IEditUserProfile,
        ): Promise<void> => {
            const changes = getUserProfileChanges(oldProfile, updatedProfile);

            if (changes !== ``) {
                const changelog: IChangeLog = {
                    byuser: updatedProfile.username,
                    affecteduser: updatedProfile.username,
                    change: `Profile Edition`,
                    description: changes,
                    section: 1,
                };

                await $.ajax({
                    data: {
                        changelog,
                    },
                    method: `POST`,
                    url: `/ChangeLog/AddChangeLog`,
                });
            }
        };

        const renderTrKeyValue = (key, value): JSX.Element => (
            <tr>
                <td>
                    <Label typography={`body`}>{key}</Label>
                </td>
                <td>
                    <Label typography={`body`}>{value}</Label>
                </td>
            </tr>
        );

        const renderMyProfile = (): JSX.Element => {
            const countryCode = getCountryCode(updatedUserProfile.phoneNumber),
                mobileNumber = getMobileNumber(updatedUserProfile.phoneNumber),
                profileAttribute: CSS.Properties = {
                    backgroundImage: updatedUserProfile.icon
                        ? `url("${updatedUserProfile.icon}")`
                        : null,
                };

            return (
                <div className={`editprofile-container`}>
                    <div
                        className={`editprofile-section1 greybackground rounded`}
                    >
                        <div className={`editprofile-titlecontainer`}>
                            <div className={`editprofile-title`}>
                                <Label typography={`heading_01`}>
                                    Profile Details
                                </Label>
                            </div>
                            {(updatedUserProfile.isPermissionProfileInfoEditEnabled ||
                                updatedUserProfile.isPermissionProfilePictureEditEnabled) && (
                                <div className={`editprofile-button-margin`}>
                                    <Button
                                        disabled={
                                            !isUserValid(updatedUserProfile)
                                        }
                                        onClick={
                                            isUserValid(updatedUserProfile)
                                                ? saveChanges
                                                : Utils.noop
                                        }
                                        type={`primary`}
                                    >
                                        Save Changes
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className={`profile-imagecontainer`}>
                            <div
                                className={`imagecontainer-image`}
                                style={profileAttribute}
                            />
                            <div
                                className={`${
                                    userProfile.icon !== updatedUserProfile.icon
                                        ? `image-edited`
                                        : ``
                                } imagecontainer-border`}
                            />
                            {updatedUserProfile.isPermissionProfilePictureEditEnabled && (
                                <div
                                    className={`imagecontainer-overlay`}
                                    onClick={selectNewIcon}
                                >
                                    <img
                                        className={`overlay-svg`}
                                        src={`/Images/UploadImage.svg`}
                                    />
                                </div>
                            )}
                            {/* Invisible input for file selection. */}
                            <input
                                accept={`image/jpeg,image/jpg,image/bmp,image/png,image/gif`}
                                id={`UserProfileImageDialog`}
                                onChange={readNewIcon}
                                ref={(aa): HTMLInputElement => (inputRef = aa)}
                                style={{
                                    display: `none`,
                                }}
                                type={`file`}
                            />
                        </div>
                        <div className={`editprofile-properties`}>
                            <div className={`properties-username`}>
                                <Label typography={`body`}>
                                    {`Username: ${userProfile.username}`}
                                </Label>
                            </div>
                            <div className={`properties properties-row`}>
                                <div className={`properties-row-item`}>
                                    <div className={`editableproperty`}>
                                        <TextField
                                            error={
                                                !isFirstNameValid(
                                                    updatedUserProfile.firstName,
                                                )
                                                    ? `Enter valid firstname`
                                                    : null
                                            }
                                            onChange={(value): void =>
                                                plainTextOrEmptyRegex.test(
                                                    value,
                                                ) &&
                                                setUpdatedUserProfile({
                                                    ...updatedUserProfile,
                                                    firstName: value,
                                                })
                                            }
                                            placeholder={`First Name`}
                                            readonly={
                                                !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                            }
                                            value={
                                                updatedUserProfile.firstName ??
                                                ``
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={`properties-row-item`}>
                                    <div className={`editableproperty`}>
                                        <TextField
                                            error={
                                                !isLastNameValid(
                                                    updatedUserProfile.lastName,
                                                )
                                                    ? `Enter valid lastname`
                                                    : null
                                            }
                                            onChange={(value): void =>
                                                plainTextOrEmptyRegex.test(
                                                    value,
                                                ) &&
                                                setUpdatedUserProfile({
                                                    ...updatedUserProfile,
                                                    lastName: value,
                                                })
                                            }
                                            placeholder={`Last Name`}
                                            readonly={
                                                !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                            }
                                            value={
                                                updatedUserProfile.lastName ??
                                                ``
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={`properties`}>
                                <div className={`editableproperty`}>
                                    <TextField
                                        error={
                                            !isJobTitleValid(
                                                updatedUserProfile.userTitle,
                                            )
                                                ? `Enter valid job title`
                                                : null
                                        }
                                        onChange={(value): void =>
                                            plainTextOrEmptyRegex.test(value) &&
                                            setUpdatedUserProfile({
                                                ...updatedUserProfile,
                                                userTitle: value,
                                            })
                                        }
                                        placeholder={`Job Title`}
                                        readonly={
                                            !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                        }
                                        value={
                                            updatedUserProfile.userTitle ?? ``
                                        }
                                    />
                                </div>
                            </div>
                            <div className={`properties`}>
                                <div className={`editableproperty`}>
                                    <TextField
                                        error={
                                            !isDepartmentValid(
                                                updatedUserProfile.department,
                                            )
                                                ? `Enter valid department`
                                                : null
                                        }
                                        onChange={(value): void =>
                                            plainTextOrEmptyRegex.test(value) &&
                                            setUpdatedUserProfile({
                                                ...updatedUserProfile,
                                                department: value,
                                            })
                                        }
                                        placeholder={`Department`}
                                        readonly={
                                            !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                        }
                                        value={
                                            updatedUserProfile.department ?? ``
                                        }
                                    />
                                </div>
                            </div>
                            {!isTenancy && (
                                <div className={`properties`}>
                                    <div className={`editableproperty`}>
                                        <TextField
                                            error={
                                                !isEmailValid(
                                                    updatedUserProfile.email,
                                                )
                                                    ? `Enter valid email`
                                                    : null
                                            }
                                            onChange={(value): void =>
                                                setUpdatedUserProfile({
                                                    ...updatedUserProfile,
                                                    email: value,
                                                })
                                            }
                                            placeholder={`Email`}
                                            readonly={
                                                !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                            }
                                            value={
                                                updatedUserProfile.email ?? ``
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                            <div className={`properties properties-row`}>
                                <div className={`properties-row-item`}>
                                    <div className={`editableproperty`}>
                                        <TextField
                                            error={
                                                !isCountryCodeValid(countryCode)
                                                    ? `Enter valid country code`
                                                    : null
                                            }
                                            onChange={(value): void =>
                                                /^\+?[0-9]{0,5}$/.test(value) &&
                                                updateCountryCode(value)
                                            }
                                            placeholder={`Country Code`}
                                            readonly={
                                                !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                            }
                                            value={countryCode ?? ``}
                                        />
                                    </div>
                                </div>
                                <div className={`properties-row-item`}>
                                    <div className={`editableproperty`}>
                                        <TextField
                                            error={
                                                !isMobileValid(mobileNumber)
                                                    ? `Enter valid mobile number`
                                                    : null
                                            }
                                            onChange={(value): void =>
                                                /^[0-9]{0,12}$/.test(value) &&
                                                updateMobileNumber(value)
                                            }
                                            placeholder={`Mobile Number`}
                                            readonly={
                                                !updatedUserProfile.isPermissionProfileInfoEditEnabled
                                            }
                                            value={mobileNumber ?? ``}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`editprofile-section2`}>
                        <div className={`greybackground rounded`}>
                            <div className={`editprofile-titlecontainer`}>
                                <div className={`editprofile-title`}>
                                    <Label typography={`heading_01`}>
                                        Password
                                    </Label>
                                </div>
                                <div className={`editprofile-button-margin`}>
                                    <Button
                                        onClick={resetPassword}
                                        type={`primary`}
                                    >
                                        Reset Password
                                    </Button>
                                </div>
                            </div>
                            <table>
                                <tbody>
                                    {renderTrKeyValue(
                                        `Last Password Change`,
                                        userProfile.lastPasswordChangedDate,
                                    )}
                                    {renderTrKeyValue(
                                        `Password Expiry Date`,
                                        userProfile.passwordExpiryDate,
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={`greybackground rounded`}>
                            <div className={`editprofile-titlecontainer`}>
                                <div className={`editprofile-title`}>
                                    <Label typography={`heading_01`}>
                                        User History
                                    </Label>
                                </div>
                            </div>
                            <table>
                                <tbody>
                                    {renderTrKeyValue(
                                        `Account Creation`,
                                        userProfile.createdDate,
                                    )}
                                    {renderTrKeyValue(
                                        `Last Login`,
                                        userProfile.lastLoginDate,
                                    )}
                                    {renderTrKeyValue(
                                        `Last Lockout Date`,
                                        userProfile.lastLockoutDate,
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            );
        };

        if (loading) {
            return <Loading />;
        } else {
            return (
                <div className={`editprofile-rootcontainer`}>
                    <NotificationProvider ref={notificationProviderRef} />
                    {cropperOpen && imageReader.result && (
                        <SubWindow
                            closeWindow={closeTowerSubWindow}
                            content={subWindowProps}
                            key={`profilecropperwindow`}
                        />
                    )}
                    <div
                        className={`editprofile-sidepanel greybackground rounded`}
                    >
                        <div className={`editprofile-title`}>
                            <Label typography={`heading_01`}>
                                Profile Page
                            </Label>
                        </div>
                        <div className={`subsection-toolcontainer`}>
                            {sideNavOptions.map((option) => (
                                <div
                                    className={`${
                                        selectedSection === option.id
                                            ? `toolcontainer-selectedsection `
                                            : ``
                                    } toolcontainer-tool`}
                                    key={option.id}
                                    onClick={(): void =>
                                        sideMenuClickEvent(option.id)
                                    }
                                >
                                    <img src={option.imgURL} />
                                    <div className={`tool-text`}>
                                        <Label typography={`body`}>
                                            {option.label}
                                        </Label>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div
                            className={`editprofile-returnbuttoncontainer editprofile-button-margin`}
                        >
                            <Button href={returnURL} type={`primary`}>
                                Return
                            </Button>
                        </div>
                    </div>
                    <div className={`editprofile-mainbody`}>
                        <div className={`scroll`}>
                            <div className={`greybackground rounded`}>
                                <div className={`editprofile-title`}>
                                    <Label typography={`heading_01`}>
                                        {sideNavOptions[selectedSection].label}
                                    </Label>
                                </div>
                            </div>
                            {selectedSection === 0 && renderMyProfile()}
                        </div>
                    </div>
                </div>
            );
        }
    };
